/**
 * CallControls — the ONLY implementation of the in-call button cluster
 * (mute · hold · transfer · hangup). Consumed by:
 *   - components/layout/ActiveCallOverlay.jsx   (variant="grid")
 *   - components/phone/SoftphonePanel.jsx       (variant="compact")
 *   - pages/comms/LeftRail.jsx                  (variant="compact",
 *                                                v2 workspace)
 *
 * Behaviour split (added iter 460):
 *   - By default, reads state/actions from useCall() (the shared bus).
 *   - When `controlled` is passed, ALL state and actions come from
 *     props. This exists for a future non-shared-bus consumer; the
 *     legacy ReceptionistCallPanel that used to demo this pattern was
 *     removed in iter 471's dead-code sweep.
 *
 * Do NOT introduce a second visual implementation anywhere else.
 */
import { Mic, MicOff, Pause, Play, PhoneOff, ArrowRightLeft } from "lucide-react";
import { useCall } from "../layout/ActiveCallOverlay";
import { cn } from "../../lib/utils";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { formatPhoneNumber } from "../../lib/utils";
import { PhoneForwarded, Building2, User, Zap } from "lucide-react";

// Semantic softphone palette — never referenced outside call UI.
const NAVY = "#143875";
const ROYAL = "#2A7CC4";
const GREEN = "#1D9E75";
const RED = "#C0392B";

/**
 * Business-line destinations for blind transfer. Same list the overlay
 * has always shipped with; centralised here so both surfaces show
 * identical options.
 */
const BUSINESS_LINES = [
  { name: "Bylyngo Interpreting", number: "+14143269510" },
  { name: "Avida Personal Care",  number: "+14143484600" },
  { name: "Bylyngo Institute",    number: "+14143269501" },
];

function TransferPicker({ children, onTransferNumber, speedDials: speedDialsProp }) {
  const { transferCall, speedDials: speedDialsFromCtx = [] } = useCall() || {};
  const speedDials = speedDialsProp ?? speedDialsFromCtx;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 max-h-[380px] overflow-y-auto" data-testid="call-controls-transfer-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider px-3 pt-2 pb-1 text-slate-400">
          Business Lines
        </DropdownMenuLabel>
        {BUSINESS_LINES.map(d => (
          <DropdownMenuItem
            key={d.number}
            onClick={() => (onTransferNumber || transferCall)?.(d.number)}
            className="flex items-center gap-2.5 py-2 mx-1 rounded-md cursor-pointer"
            data-testid={`call-controls-transfer-${d.number}`}
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${ROYAL}22` }}>
              <Building2 className="w-4 h-4" style={{ color: ROYAL }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{d.name}</p>
              <p className="text-[11px] text-slate-400 font-mono">{formatPhoneNumber(d.number)}</p>
            </div>
            <PhoneForwarded className="w-3.5 h-3.5 text-slate-400" />
          </DropdownMenuItem>
        ))}

        {speedDials.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider px-3 pt-1 pb-1 text-slate-400">
              Speed Dials
            </DropdownMenuLabel>
            {speedDials.slice(0, 8).map(sd => (
              <DropdownMenuItem
                key={sd.id}
                onClick={() => (onTransferNumber || transferCall)?.(sd.phone_number)}
                className="flex items-center gap-2.5 py-2 mx-1 rounded-md cursor-pointer"
                data-testid={`call-controls-transfer-sd-${sd.id}`}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${ROYAL}22` }}>
                  <Zap className="w-4 h-4" style={{ color: ROYAL }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{sd.name}</p>
                  <p className="text-[11px] text-slate-400 font-mono">{formatPhoneNumber(sd.phone_number)}</p>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * <CallControls />
 *
 * Props:
 *   variant   — "compact" (icon-only pills, for the gold active-call bar)
 *             | "grid"    (labeled tiles in a 4-col grid, for the overlay)
 *   onDark    — true when parent has a dark background; flips icon color
 *               contrast for non-semantic (mute/hold) buttons only.
 */
export default function CallControls({ variant = "compact", onDark = false, controlled = null, hideHangup = false }) {
  // Controlled mode — all state and actions come from the parent.
  // The legacy ReceptionistCallPanel used to own its own Twilio Device
  // and drive this cluster via `controlled`; that panel was removed in
  // iter 471's dead-code sweep, but the API is kept for any future
  // consumer that lives outside the shared useCall bus.
  //
  // Uncontrolled mode — the historical default. Reads from useCall().
  const ctx = useCall();
  const state = controlled ?? (ctx || {});
  if (!controlled && !ctx) return null;
  const { activeCall, isMuted, toggleMute, isOnHold, toggleHold, endCall } = state;
  if (!activeCall) return null;

  const isGrid = variant === "grid";

  // ── Grid variant — big round RingCentral-style buttons. Rendered via
  // display:contents so the parent (ActiveCallOverlay) can lay Mute / Hold /
  // Transfer out in ONE grid together with its own Keypad / Park buttons.
  if (isGrid) {
    const disc = "w-14 h-14 rounded-full flex items-center justify-center transition-colors";
    const discIdle = "bg-slate-100 text-slate-700 group-hover:bg-slate-200";
    const label = "text-xs font-medium text-slate-600";
    const btn = "flex flex-col items-center gap-1.5 group focus-visible:outline-none";
    return (
      <div className="contents" data-testid="call-controls" data-variant={variant}>
        {/* Mute */}
        <button onClick={toggleMute} title={isMuted ? "Unmute" : "Mute"} data-testid="call-controls-mute" className={btn}>
          <span className={cn(disc, isMuted ? "text-white" : discIdle)} style={isMuted ? { background: RED } : undefined}>
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </span>
          <span className={label}>{isMuted ? "Unmute" : "Mute"}</span>
        </button>

        {/* Hold */}
        <button onClick={toggleHold} title={isOnHold ? "Resume" : "Hold"} data-testid="call-controls-hold" className={btn}>
          <span className={cn(disc, isOnHold ? "text-white" : discIdle)} style={isOnHold ? { background: ROYAL } : undefined}>
            {isOnHold ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
          </span>
          <span className={label}>{isOnHold ? "Resume" : "Hold"}</span>
        </button>

        {/* Transfer */}
        <TransferPicker onTransferNumber={state.transferCall} speedDials={state.speedDials}>
          <button title="Transfer" data-testid="call-controls-transfer" className={btn}>
            <span className={cn(disc, discIdle)}><ArrowRightLeft className="w-6 h-6" /></span>
            <span className={label}>Transfer</span>
          </button>
        </TransferPicker>

        {/* Hang up — optional (overlay renders its own prominent red button) */}
        {!hideHangup && (
          <button onClick={endCall} title="End call" data-testid="call-controls-hangup" className={btn}>
            <span className={cn(disc, "text-white")} style={{ background: RED }}><PhoneOff className="w-6 h-6" /></span>
            <span className={label}>End</span>
          </button>
        )}
      </div>
    );
  }

  // ── Compact variant — icon-only pills (gold active-call bar / left rail).
  const neutralBase = "w-10 h-10 rounded-lg flex items-center justify-center transition-colors";
  const neutralIdle = onDark
    ? "bg-slate-800 text-slate-200 hover:bg-slate-700"
    : "bg-white/60 hover:bg-white text-[color:var(--foreground,#1e293b)] border border-black/5";

  return (
    <div className="flex items-center gap-1.5" data-testid="call-controls" data-variant={variant}>
      {/* Mute */}
      <button
        onClick={toggleMute}
        title={isMuted ? "Unmute" : "Mute"}
        data-testid="call-controls-mute"
        className={cn(neutralBase, isMuted ? "text-white" : neutralIdle)}
        style={isMuted ? { background: RED } : undefined}
      >
        {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </button>

      {/* Hold */}
      <button
        onClick={toggleHold}
        title={isOnHold ? "Resume" : "Hold"}
        data-testid="call-controls-hold"
        className={cn(neutralBase, isOnHold ? "text-white" : neutralIdle)}
        style={isOnHold ? { background: ROYAL } : undefined}
      >
        {isOnHold ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
      </button>

      {/* Transfer */}
      <TransferPicker onTransferNumber={state.transferCall} speedDials={state.speedDials}>
        <button
          title="Transfer"
          data-testid="call-controls-transfer"
          className={cn(neutralBase, neutralIdle)}
          style={{ color: onDark ? undefined : ROYAL }}
        >
          <ArrowRightLeft className="w-4 h-4" />
        </button>
      </TransferPicker>

      {/* Hang up — always red, always last. */}
      {!hideHangup && (
        <button
          onClick={endCall}
          title="End call"
          data-testid="call-controls-hangup"
          className={cn(neutralBase, "text-white")}
          style={{ background: RED }}
        >
          <PhoneOff className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
